import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import avatarImg from '../../assets/img/avatar.png';
import loggedInAvatarImg from '../../assets/img/avatar-1.png';
import LocalMallIcon from '@mui/icons-material/LocalMallOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LocationPinIcon from '@mui/icons-material/LocationPin';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import WavingHandIcon from '@mui/icons-material/WavingHand';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import PersonOutlineRoundedIcon from '@mui/icons-material/PersonOutlineRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import { useAppContext } from '../../contexts/AppContext';

export default function Header() {
  const navigate = useNavigate();
  const location = useLocation();
  const { cartItems, user, logoutUser, cartFxToken } = useAppContext();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [isCartAnimating, setIsCartAnimating] = useState(false);

  const isMenuOpen = Boolean(menuAnchorEl);
  const isLoggedIn = Boolean(user);

  const displayName = useMemo(() => {
    if (user?.name?.trim()) {
      return user.name.trim();
    }

    if (user?.email?.trim()) {
      return user.email.trim().split('@')[0];
    }

    if (user?.mobile?.trim()) {
      return user.mobile.trim();
    }

    return 'Guest';
  }, [user]);

  const welcomeLine = isLoggedIn ? `Hi, ${displayName}` : 'Hi, Shopper';
  const subtitle = isLoggedIn
    ? user?.email || user?.mobile || 'Signed in customer'
    : 'Fresh snacks delivered fast';
  const locationLabel = isLoggedIn
    ? user?.address || 'Your saved address'
    : 'Delivering fresh picks near you';
  const avatarLabel = isLoggedIn ? displayName : 'Guest';
  const avatarSrc = isLoggedIn ? loggedInAvatarImg : avatarImg;
  const hasSearchBelowHeader =
    location.pathname === '/' || location.pathname === '/search';

  const handleMenuOpen = (event) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logoutUser();
    navigate('/signin');
  };

  useEffect(() => {
    if (!cartFxToken) {
      return undefined;
    }

    setIsCartAnimating(true);
    const timeoutId = window.setTimeout(() => {
      setIsCartAnimating(false);
    }, 520);

    return () => window.clearTimeout(timeoutId);
  }, [cartFxToken]);

  useEffect(() => {
    if (!isMenuOpen) {
      return undefined;
    }

    const handleWindowScroll = () => {
      setMenuAnchorEl(null);
    };

    window.addEventListener('scroll', handleWindowScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleWindowScroll);
    };
  }, [isMenuOpen]);

  useEffect(() => {
    setMenuAnchorEl(null);
  }, [location.pathname]);

  return (
    <AppBar
      position='static'
      sx={{
        background:
          'radial-gradient(circle at top right, rgba(255,255,255,0.18), transparent 26%), linear-gradient(180deg, #ff5858 0%, #ff4747 100%)',
        borderBottomLeftRadius: '28px',
        borderBottomRightRadius: '28px',
        boxShadow: '0 18px 40px rgba(255, 81, 81, 0.24)',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          px: { xs: 1.5, sm: 2.25 },
          py: hasSearchBelowHeader
            ? { xs: 1.15, sm: 1.4 }
            : { xs: 1, sm: 1.15 },
          gap: 1.1,
          alignItems: 'flex-start',
          position: 'relative',
          minHeight: hasSearchBelowHeader ? (isLoggedIn ? 170 : 142) : 110,
          '&::before': {
            content: '""',
            position: 'absolute',
            width: 240,
            height: 240,
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.16)',
            right: -90,
            bottom: -130,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            width: 170,
            height: 170,
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.14)',
            right: -52,
            bottom: -94,
          },
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            minWidth: 0,
            position: 'relative',
            zIndex: 1,
          }}
        >
          <Avatar
            src={avatarSrc}
            alt={avatarLabel}
            sx={{
              width: 44,
              height: 44,
              border: '2px solid rgba(255,255,255,0.4)',
              boxShadow: '0 10px 20px rgba(126, 19, 19, 0.22)',
            }}
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              ml: 1,
              gap: 0.65,
              minWidth: 0,
            }}
          >
            {isLoggedIn ? (
              <>
                <Typography
                  sx={{
                    color: 'rgba(255,255,255,0.78)',
                    fontWeight: 600,
                    fontSize: '0.68rem',
                    letterSpacing: '0.04em',
                  }}
                >
                  Location
                </Typography>
                <Box
                  component={Link}
                  to='/'
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    textDecoration: 'none',
                    minWidth: 0,
                  }}
                >
                  <LocationPinIcon sx={{ fontSize: '18px', color: '#fff' }} />
                  <Typography
                    sx={{
                      color: '#fff',
                      ml: 0.75,
                      fontWeight: 700,
                      fontSize: '0.9rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {locationLabel}
                  </Typography>
                  <KeyboardArrowDownIcon
                    sx={{ color: '#fff', fontSize: '16px' }}
                  />
                </Box>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}
                >
                  <WavingHandIcon
                    sx={{ color: '#ffe58a', fontSize: '18px', mr: 0.75 }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: { xs: '0.92rem', sm: '0.98rem' },
                        lineHeight: 1.15,
                      }}
                    >
                      {welcomeLine}
                    </Typography>
                    <Typography
                      sx={{
                        color: 'rgba(255,255,255,0.82)',
                        fontSize: '0.7rem',
                        lineHeight: 1.25,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {subtitle}
                    </Typography>
                  </Box>
                </Box>
              </>
            ) : (
              <>
                <Typography
                  sx={{
                    color: 'rgba(255,255,255,0.78)',
                    fontWeight: 700,
                    fontSize: '0.72rem',
                    letterSpacing: '0.08em',
                    textTransform: 'uppercase',
                  }}
                >
                  Welcome
                </Typography>
                <Typography
                  sx={{
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: { xs: '0.98rem', sm: '1.02rem' },
                    lineHeight: 1.2,
                  }}
                >
                  Discover crunchy favorites
                </Typography>
                <Typography
                  sx={{
                    color: 'rgba(255,255,255,0.84)',
                    fontSize: '0.72rem',
                    lineHeight: 1.3,
                  }}
                >
                  {subtitle}
                </Typography>
              </>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.75,
            position: 'relative',
            zIndex: 1,
          }}
        >
          {isLoggedIn ? (
            <IconButton
              sx={{
                width: 38,
                height: 38,
                backgroundColor: 'rgba(255,255,255,0.18)',
                color: '#fff',
                borderRadius: '12px',
              }}
            >
              <Badge
                variant='dot'
                color='warning'
                overlap='circular'
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <NotificationsRoundedIcon fontSize='small' />
              </Badge>
            </IconButton>
          ) : null}

          <Button
            color='inherit'
            component={Link}
            to='/cart'
            className={`app-pressable${isCartAnimating ? ' app-cart-bump' : ''}`}
            sx={{
              minWidth: 'auto',
              backgroundColor: 'rgba(255,255,255,0.18)',
              borderRadius: '12px',
              width: 38,
              height: 38,
              p: 0,
            }}
          >
            <Badge
              badgeContent={cartItems.length}
              color='primary'
              className={isCartAnimating ? 'app-cart-badgePulse' : ''}
            >
              <LocalMallIcon sx={{ color: '#fff' }} />
            </Badge>
          </Button>

          <Button
            color='inherit'
            sx={{
              minWidth: 'auto',
              backgroundColor: 'rgba(255,255,255,0.18)',
              borderRadius: '12px',
              width: 38,
              height: 38,
              p: 0,
            }}
            onClick={handleMenuOpen}
            aria-controls={isMenuOpen ? 'header-account-menu' : undefined}
            aria-haspopup='true'
            aria-expanded={isMenuOpen ? 'true' : undefined}
          >
            <MoreVertIcon sx={{ color: '#fff' }} />
          </Button>
        </Box>
      </Box>

      {isLoggedIn ? (
        <Chip
          icon={<PersonOutlineRoundedIcon sx={{ color: '#fff !important' }} />}
          label='Logged in'
          sx={{
            position: 'absolute',
            right: { xs: 20, sm: 28 },
            bottom: hasSearchBelowHeader ? 10 : 12,
            borderRadius: '999px',
            backgroundColor: 'rgba(255,255,255,0.16)',
            color: '#fff',
            fontWeight: 700,
            height: 28,
          }}
        />
      ) : null}

      <Menu
        id='header-account-menu'
        anchorEl={menuAnchorEl}
        open={isMenuOpen}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {isLoggedIn
          ? [
              <MenuItem
                key='user-summary'
                disabled
                sx={{ opacity: '1 !important' }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 700, color: '#161616' }}>
                    {displayName}
                  </Typography>
                  <Typography sx={{ fontSize: '12px', color: '#7D7D7D' }}>
                    {subtitle}
                  </Typography>
                </Box>
              </MenuItem>,
              <MenuItem key='logout' onClick={handleLogout}>
                <LogoutRoundedIcon sx={{ mr: 1, fontSize: 20 }} />
                Sign out
              </MenuItem>,
            ]
          : [
              <MenuItem
                key='signin'
                component={Link}
                to='/signin'
                onClick={handleMenuClose}
              >
                <LoginRoundedIcon sx={{ mr: 1, fontSize: 20 }} />
                Sign in
              </MenuItem>,
              <MenuItem
                key='signup'
                component={Link}
                to='/signup'
                onClick={handleMenuClose}
              >
                <PersonOutlineRoundedIcon sx={{ mr: 1, fontSize: 20 }} />
                Sign up
              </MenuItem>,
            ]}
      </Menu>
    </AppBar>
  );
}
